/**
 * Quote IV Indicator
 *
 * Displays ATM implied volatility as a line overlaid on the main chart
 * using the built-in left price scale for independent scale control
 * (scroll/zoom the left axis to adjust IV range independently).
 *
 * Optional rolling standard deviation bands (±2σ) computed from a
 * configurable lookback window (default 20 periods after downsampling).
 *
 * Uses one series per trading day to avoid lines connecting across
 * overnight gaps (lightweight-charts v5 does not support whitespace
 * gaps for line-based series).
 *
 * Historical/multi-day data is fetched by the chart component and passed
 * in via enable(segments) / update(segments) as an array of per-day arrays.
 * Live updates for the current day come from SSE endpoint /sse/indicators/quote-iv.
 */

import { LineSeries } from 'lightweight-charts';
import { unifiedSSE } from '../hooks/useUnifiedSSE';

// Map timeframe strings to seconds
const TF_SECONDS = {
  '1s': 1, '5s': 5, '15s': 15, '30s': 30,
  '1m': 60, '5m': 300, '15m': 900, '30m': 1800, '1h': 3600,
};

const STD_DEV_PERIOD = 20; // Rolling lookback window (in downsampled bars)

export class QuoteIVIndicator {
  /**
   * @param {object} chart - The main lightweight-charts IChartApi instance
   * @param {string} underlying - The page's selected underlying ('spx',
   *   'spy' or 'es'); the shared 'quote-iv' SSE channel carries every
   *   underlying's ticks and is filtered against this. The chart recreates
   *   the indicator on underlying switch (teardown-recreate idiom).
   */
  constructor(chart, underlying = 'spx') {
    this.chart = chart;
    this.underlying = underlying;
    this.enabled = false;

    this._segments = [];      // array of per-day point arrays
    this._seriesList = [];    // one LineSeries per day segment (IV line)
    this._upperBandList = []; // one LineSeries per day segment (upper σ band)
    this._lowerBandList = []; // one LineSeries per day segment (lower σ band)
    this._realizedSegments = []; // per-day realized-vol arrays (pre-bucketed by the chart)
    this._realizedList = []; // one LineSeries per day segment (realized vol)
    this._sseUnsubs = [];
    this._interval = 300;     // downsample interval in seconds (default 5m)
    this._showStdDev = false; // whether std dev bands are visible
    this._showRealized = false; // whether the realized-vol companion line is visible
  }

  // ── Series management ───────────────────────────────────────────────────

  _seriesOptions(isFirst) {
    return {
      priceScaleId: 'left',
      color: '#ffffff',
      lineWidth: 2,
      lastValueVisible: isFirst,
      priceLineVisible: false,
      crosshairMarkerVisible: true,
      title: isFirst ? 'IV' : '',
      priceFormat: {
        type: 'custom',
        formatter: (value) => (value * 100).toFixed(1) + '%',
      },
    };
  }

  _bandSeriesOptions() {
    return {
      priceScaleId: 'left',
      color: 'rgba(100, 181, 246, 0.5)',
      lineWidth: 1,
      lineStyle: 2, // dashed
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      title: '',
      priceFormat: {
        type: 'custom',
        formatter: (value) => (value * 100).toFixed(1) + '%',
      },
    };
  }

  _ensureLeftScale() {
    this.chart.priceScale('left').applyOptions({
      visible: true,
      borderVisible: true,
      borderColor: '#2a2a2a',
      autoScale: true,
    });
  }

  _removeAllSeries() {
    for (const s of this._seriesList) {
      try { this.chart.removeSeries(s); } catch (e) { /* may already be detached */ }
    }
    this._seriesList = [];
    this._removeBandSeries();
    this._removeRealizedSeries();
  }

  _removeRealizedSeries() {
    for (const s of this._realizedList) {
      try { this.chart.removeSeries(s); } catch (e) { /* may already be detached */ }
    }
    this._realizedList = [];
  }

  _realizedSeriesOptions(isLast) {
    return {
      priceScaleId: 'left',
      color: '#f97316', // orange: delivered movement vs the white implied line
      lineWidth: 1,
      lastValueVisible: isLast,
      priceLineVisible: false,
      crosshairMarkerVisible: true,
      title: isLast ? 'RV' : '',
      priceFormat: {
        type: 'custom',
        formatter: (value) => (value * 100).toFixed(1) + '%',
      },
    };
  }

  /**
   * Render the realized-vol companion segments (already bucketed and
   * annualized by the chart component; descriptive comparison only).
   */
  _applyRealized() {
    if (!this._showRealized) {
      if (this._realizedList.length > 0) this._removeRealizedSeries();
      return;
    }
    const numSegments = this._realizedSegments.length;
    if (this._realizedList.length !== numSegments) {
      this._removeRealizedSeries();
      for (let i = 0; i < numSegments; i++) {
        this._realizedList.push(
          this.chart.addSeries(LineSeries, this._realizedSeriesOptions(i === numSegments - 1))
        );
      }
    }
    for (let i = 0; i < numSegments; i++) {
      this._realizedList[i].setData(this._realizedSegments[i]);
    }
  }

  _removeBandSeries() {
    for (const s of this._upperBandList) {
      try { this.chart.removeSeries(s); } catch (e) { /* may already be detached */ }
    }
    for (const s of this._lowerBandList) {
      try { this.chart.removeSeries(s); } catch (e) { /* may already be detached */ }
    }
    this._upperBandList = [];
    this._lowerBandList = [];
  }

  // ── Standard deviation bands ──────────────────────────────────────────

  /**
   * Compute rolling mean ± 2σ bands from downsampled IV points.
   * Returns { upper: [{time, value}], lower: [{time, value}] }
   */
  _computeBands(points) {
    const period = STD_DEV_PERIOD;
    const minBars = 2; // minimum bars before showing bands
    if (!points || points.length < minBars) return { upper: [], lower: [] };

    const upper = [];
    const lower = [];

    for (let i = minBars - 1; i < points.length; i++) {
      // Expanding window until we reach full period, then rolling
      const windowStart = Math.max(0, i - period + 1);
      const windowSize = i - windowStart + 1;

      // Compute mean over window
      let sum = 0;
      for (let j = windowStart; j <= i; j++) {
        sum += points[j].value;
      }
      const mean = sum / windowSize;

      // Compute std dev over window
      let sqSum = 0;
      for (let j = windowStart; j <= i; j++) {
        const diff = points[j].value - mean;
        sqSum += diff * diff;
      }
      const stdDev = Math.sqrt(sqSum / windowSize);

      upper.push({ time: points[i].time, value: mean + 2 * stdDev });
      lower.push({ time: points[i].time, value: mean - 2 * stdDev });
    }

    return { upper, lower };
  }

  // ── Downsampling ────────────────────────────────────────────────────────

  setTimeframe(tf) {
    const newInterval = TF_SECONDS[tf] || 300;
    if (newInterval !== this._interval) {
      this._interval = newInterval;
      this._applyData();
    }
  }

  _downsample(points) {
    if (!points || points.length === 0) return points;
    const interval = this._interval;

    const buckets = new Map();
    for (const p of points) {
      const bucket = Math.floor(p.time / interval) * interval;
      buckets.set(bucket, { time: bucket, value: p.value });
    }
    return Array.from(buckets.values()).sort((a, b) => a.time - b.time);
  }

  _applyData() {
    const numSegments = this._segments.length;

    // Reconcile IV line series count
    if (this._seriesList.length !== numSegments) {
      this._removeAllSeries();
      for (let i = 0; i < numSegments; i++) {
        const series = this.chart.addSeries(LineSeries, this._seriesOptions(i === numSegments - 1));
        this._seriesList.push(series);
      }
      if (numSegments > 0) this._ensureLeftScale();
    }

    // Set data on each IV segment series + compute bands
    const downsampledSegments = [];
    for (let i = 0; i < numSegments; i++) {
      const downsampled = this._downsample(this._segments[i]);
      this._seriesList[i].setData(downsampled);
      downsampledSegments.push(downsampled);
    }

    // Apply std dev bands + the realized-vol companion (the series wipe
    // above removes realized too, so it must be re-applied here or an IV
    // segment-count change silently drops the RV line)
    this._applyBands(downsampledSegments);
    this._applyRealized();
  }

  _applyBands(downsampledSegments) {
    const numSegments = downsampledSegments.length;

    if (!this._showStdDev) {
      // Remove band series if they exist
      if (this._upperBandList.length > 0 || this._lowerBandList.length > 0) {
        this._removeBandSeries();
      }
      return;
    }

    // Reconcile band series count
    if (this._upperBandList.length !== numSegments) {
      this._removeBandSeries();
      for (let i = 0; i < numSegments; i++) {
        this._upperBandList.push(this.chart.addSeries(LineSeries, this._bandSeriesOptions()));
        this._lowerBandList.push(this.chart.addSeries(LineSeries, this._bandSeriesOptions()));
      }
    }

    // Set band data
    for (let i = 0; i < numSegments; i++) {
      const { upper, lower } = this._computeBands(downsampledSegments[i]);
      this._upperBandList[i].setData(upper);
      this._lowerBandList[i].setData(lower);
    }
  }

  // ── SSE connection ──────────────────────────────────────────────────────

  _connectSSE() {
    this._disconnectSSE();

    this._sseUnsubs.push(
      unifiedSSE.subscribe('quote-iv', 'quote_iv_update', (data) => {
        try {
          if (data.atm_iv == null || data.time == null) return;
          // The shared channel carries spx/spy/es ticks; keep only the
          // selected underlying's. Untagged ticks (pre-restart backend)
          // are accepted on SPX only, so an SPX page never blanks in the
          // deploy window while SPY/ES pages stop ingesting bleed.
          const selUl = this.underlying || 'spx';
          if (data.underlying === undefined) {
            if (selUl !== 'spx') return;
          } else if (data.underlying !== selUl) {
            return;
          }

          // Snap to candle boundary so IV doesn't stretch the time axis
          const bucket = Math.floor(data.time / this._interval) * this._interval;

          // Update the last series (current day)
          const lastSeries = this._seriesList[this._seriesList.length - 1];
          if (lastSeries) {
            lastSeries.update({ time: bucket, value: data.atm_iv });
          }

          // Store raw data in the last segment
          if (this._segments.length > 0) {
            const lastSeg = this._segments[this._segments.length - 1];
            lastSeg.push({ time: data.time, value: data.atm_iv });
            if (lastSeg.length > 28800) lastSeg.shift();

            // Update std dev bands for the last segment
            if (this._showStdDev && this._upperBandList.length > 0) {
              const lastIdx = this._upperBandList.length - 1;
              const downsampled = this._downsample(lastSeg);
              const { upper, lower } = this._computeBands(downsampled);
              if (upper.length > 0) {
                this._upperBandList[lastIdx].setData(upper);
                this._lowerBandList[lastIdx].setData(lower);
              }
            }
          }
        } catch (e) {
          console.error('[QuoteIVIndicator] Update handler error:', e);
        }
      })
    );

    console.log('[QuoteIVIndicator] SSE connected (unified)');
  }

  _disconnectSSE() {
    if (this._sseUnsubs.length > 0) {
      this._sseUnsubs.forEach(unsub => unsub());
      this._sseUnsubs = [];
      console.log('[QuoteIVIndicator] SSE disconnected');
    }
  }

  // ── Public API ──────────────────────────────────────────────────────────

  /**
   * Toggle standard deviation bands on/off.
   * @param {boolean} show
   */
  setStdDev(show) {
    if (this._showStdDev === show) return;
    this._showStdDev = show;
    if (this.enabled) {
      this._applyData();
    }
  }

  /**
   * Toggle the realized-vol companion line on/off.
   */
  setShowRealized(show) {
    if (this._showRealized === !!show) return;
    this._showRealized = !!show;
    if (this.enabled) this._applyRealized();
  }

  /**
   * Replace the realized-vol per-day segments (computed by the chart
   * from its own candles) and re-render if visible.
   */
  setRealized(segments) {
    this._realizedSegments = segments || [];
    if (this.enabled) this._applyRealized();
  }

  /**
   * Append one live realized-vol point. Draw-only: the chart component
   * owns the segment arrays (setRealized stores them by reference and the
   * chart mutates them in place), so hidden/disabled states catch up via
   * _applyRealized on the next enable/toggle. newSegment starts a fresh
   * series and moves the 'RV' last-value marker onto it.
   */
  appendRealized(point, newSegment) {
    if (!this.enabled || !this._showRealized) return;
    try {
      if (newSegment || this._realizedList.length === 0) {
        const prevLast = this._realizedList[this._realizedList.length - 1];
        if (prevLast) prevLast.applyOptions(this._realizedSeriesOptions(false));
        const s = this.chart.addSeries(LineSeries, this._realizedSeriesOptions(true));
        this._realizedList.push(s);
        s.update(point);
      } else {
        this._realizedList[this._realizedList.length - 1].update(point);
      }
    } catch (e) {
      // update() throws if the point is older than the series tail; a
      // full redraw follows on the next setRealized either way
      console.warn('[QuoteIVIndicator] appendRealized failed:', e.message);
    }
  }

  /**
   * Repaint the forming-bar provisional realized-vol point. Draw-only and
   * uncommitted: the chart recomputes it per live tick, and the completed
   * bar's appendRealized replaces it at the same timestamp. Deliberately
   * never creates a series (a provisional point must not open a segment).
   */
  updateRealizedForming(point) {
    if (!this.enabled || !this._showRealized) return;
    const last = this._realizedList[this._realizedList.length - 1];
    if (!last) return;
    try {
      last.update(point);
    } catch (e) {
      // out-of-order forming tick; the committed redraw follows
    }
  }

  /**
   * Enable the indicator with pre-fetched IV data segments.
   * @param {Array<Array<{time: number, value: number}>>} segments - per-day arrays
   */
  enable(segments) {
    this.enabled = true;

    this._segments = segments || [];
    this._applyData();

    // Connect SSE for live updates on current day
    this._connectSSE();

    const totalPoints = this._segments.reduce((sum, s) => sum + s.length, 0);
    console.log('[QuoteIVIndicator] Enabled with', totalPoints, 'points across', this._segments.length, 'days');
  }

  /**
   * Update the indicator with new data segments.
   * @param {Array<Array<{time: number, value: number}>>} segments - per-day arrays
   */
  update(segments) {
    if (!this.enabled) return;

    this._segments = segments || [];
    this._applyData();
  }

  disable() {
    if (!this.enabled) return;

    this._disconnectSSE();
    this._removeAllSeries();
    this._segments = [];
    this.enabled = false;

    console.log('[QuoteIVIndicator] Disabled');
  }

  isEnabled() {
    return this.enabled;
  }
}

export default QuoteIVIndicator;
